/**
 * Affiliate Landing Page for Booker
 * Material-UI styled - matches project theme
 * Redirects to central for application & dashboard
 *
 * Created: 2025-01-20
 * Updated: 2025-01-23 - Converted to Material-UI
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  TrendingUp,
  AttachMoney,
  People,
  EmojiEvents,
  CheckCircle
} from '@mui/icons-material';
import SharedHeader from './common/SharedHeader';

// ========================================
// BOOKER APP CONFIGURATION
// ========================================
const APP_CONFIG = {
  appName: 'Booker',
  appDescription: 'Smart appointment scheduling platform',
  primaryColor: '#3B82F6', // Booker's blue brand color
  useCase: 'streamline appointment bookings and reduce no-shows',
  benefitExample: 'manage bookings, send automated reminders, track clients, and grow their business',

  // Central dashboard URLs (absolute URLs to central app)
  centralDashboardUrl: 'https://paymentgateway-ab783.web.app/affiliate/dashboard',
  centralApplicationUrl: 'https://paymentgateway-ab783.web.app/affiliate/apply',
};
// ========================================

const AffiliateLandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleApplyNow = () => {
    window.location.href = APP_CONFIG.centralApplicationUrl;
  };

  const handleViewDashboard = () => {
    window.location.href = APP_CONFIG.centralDashboardUrl;
  };

  const features = [
    {
      icon: <AttachMoney sx={{ fontSize: 48, color: theme.palette.success.main }} />,
      title: 'High Commissions',
      description: 'Earn 20% on credit packages and 25% recurring for 4 months on subscriptions. Top performers get 30% for 6 months!'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Real-Time Tracking',
      description: 'Monitor your referrals, earnings, and commission in real-time through our comprehensive affiliate dashboard.'
    },
    {
      icon: <People sx={{ fontSize: 48, color: theme.palette.secondary.main }} />,
      title: 'Promote 30+ Apps',
      description: 'Your referral code works across all FutureSense apps - from invoicing to AI calling to payroll.'
    }
  ];

  const commissionStructure = [
    {
      type: 'Credit Packages',
      rate: '20%',
      duration: 'One-time',
      example: '$299 package → $59.80 commission',
      color: 'success'
    },
    {
      type: 'Subscriptions',
      rate: '25%',
      duration: '4 months recurring',
      example: '$99/mo → $99 total (4 months)',
      color: 'primary'
    },
    {
      type: 'Top Performer Bonus',
      rate: '30%',
      duration: '6 months recurring',
      example: '$500+/month referrals',
      color: 'secondary'
    }
  ];

  const steps = [
    { number: '1', title: 'Apply', description: 'Submit your affiliate application' },
    { number: '2', title: 'Get Approved', description: 'We review within 24-48 hours' },
    { number: '3', title: 'Share Links', description: 'Promote with your unique referral code' },
    { number: '4', title: 'Earn Money', description: 'Get paid monthly via PayPal or Stripe' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Helmet>
        <title>Affiliate Program — Earn with FutureSense AI</title>
        <meta name="description" content="Join the FutureSense AI affiliate program. Earn 25% recurring commission for 4 months on every subscription referral. Free to join, paid monthly." />
        <link rel="canonical" href="https://nexus.futuresenseai.com/affiliate" />
        <meta property="og:title" content="Affiliate Program — Earn with FutureSense AI" />
        <meta property="og:description" content="Earn 25% recurring commission referring businesses to FutureSense AI. Apply free today." />
        <meta property="og:url" content="https://nexus.futuresenseai.com/affiliate" />
        <meta property="og:image" content="https://nexus.futuresenseai.com/logo512.png" />
      </Helmet>
      <SharedHeader />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mt: 8
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Chip
              label="💰 Affiliate Program"
              sx={{
                mb: 3,
                bgcolor: alpha(theme.palette.common.white, 0.2),
                color: 'white',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 3 }}>
              Refer {APP_CONFIG.appName} & Earn Big
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto', opacity: 0.95 }}>
              Help businesses {APP_CONFIG.useCase} with {APP_CONFIG.appName} and earn{' '}
              <Box component="span" sx={{ fontWeight: 700, color: theme.palette.success.light }}>
                25% recurring commission for 4 months
              </Box>{' '}
              on every subscription you refer.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleApplyNow}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                Apply to Become an Affiliate
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleViewDashboard}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: alpha(theme.palette.common.white, 0.1)
                  }
                }}
              >
                View Affiliate Dashboard
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: alpha(feature.icon.props.sx.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Commission Structure */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ fontWeight: 700, mb: 6 }}>
            Commission Structure
          </Typography>
          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Purchase Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Commission Rate</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Example Earnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commissionStructure.map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.rate}
                        color={row.color}
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>{row.duration}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.example}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* How It Works */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" sx={{ fontWeight: 700, mb: 6 }}>
          How It Works
        </Typography>
        <Grid container spacing={4}>
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 700
                  }}
                >
                  {step.number}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Use Case Example */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Card elevation={3}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                Why Businesses Love {APP_CONFIG.appName}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                {APP_CONFIG.appName} helps businesses {APP_CONFIG.benefitExample}. It's easy to promote because
                it solves real problems and delivers immediate value.
              </Typography>
              <Chip
                icon={<EmojiEvents />}
                label="Average affiliate earns $500-2000/month"
                color="success"
                sx={{ fontSize: '1rem', py: 3, px: 2, fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Ready to Start Earning?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join hundreds of affiliates already earning with FutureSense AI
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleApplyNow}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9)
                  }
                }}
              >
                Apply Now - It's Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleViewDashboard}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: alpha(theme.palette.common.white, 0.1)
                  }
                }}
              >
                Login to Dashboard
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer Note */}
      <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          Questions? Contact us at{' '}
          <Box
            component="a"
            href="mailto:affiliates@futuresenseai.com"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            affiliates@futuresenseai.com
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default AffiliateLandingPage;
